import apiClient from "@/services/api-client";
import type {
  SystemModule,
  TenantModuleList,
  ModuleAccessResult,
  EnableModulePayload,
  DisableModulePayload,
  BulkAssignModulesPayload,
  CreateSystemModulePayload,
  PageOverrideMode,
  PagePolicy,
  TenantPageOverrideList,
} from "@/types/modules";

// ═══════════════════════════════════════════════════════════════════════════
// MODULE SERVICE
// API service for module management operations
// ═══════════════════════════════════════════════════════════════════════════

const mutationConfig = () => ({
  headers: { "Idempotency-Key": crypto.randomUUID() },
});

export const moduleService = {
  /**
   * Get all system modules
   */
  getAllModules: async (): Promise<SystemModule[]> => {
    const response = await apiClient.get<SystemModule[]>("/modules");
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Seed/initialize system modules
   */
  seedModules: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
    }>("/modules/seed", {}, mutationConfig());
    return response.data;
  },

  /**
   * Create a system module
   */
  createSystemModule: async (
    payload: CreateSystemModulePayload,
  ): Promise<SystemModule> => {
    const response = await apiClient.post<SystemModule>(
      "/modules",
      payload,
      mutationConfig(),
    );
    return response.data;
  },

  /**
   * Get modules for a specific tenant
   */
  getTenantModules: async (tenantId: string): Promise<TenantModuleList> => {
    const response = await apiClient.get<TenantModuleList>(
      `/modules/tenants/${tenantId}`,
    );
    return response.data;
  },

  getPagePolicies: async (): Promise<PagePolicy[]> => {
    const response = await apiClient.get<PagePolicy[]>("/modules/pages");
    return Array.isArray(response.data) ? response.data : [];
  },

  getTenantPageOverrides: async (
    tenantId: string,
  ): Promise<TenantPageOverrideList> => {
    const response = await apiClient.get<TenantPageOverrideList>(
      `/modules/tenants/${tenantId}/page-overrides`,
    );
    return {
      accessVersion: Number(response.data?.accessVersion ?? 0),
      overrides: Array.isArray(response.data?.overrides)
        ? response.data.overrides
        : [],
    };
  },

  setTenantPageOverride: async (
    tenantId: string,
    pageKey: string,
    payload: { mode: PageOverrideMode; reason: string; expiresAt?: string },
  ) => {
    const response = await apiClient.put(
      `/modules/tenants/${tenantId}/page-overrides/${encodeURIComponent(pageKey)}`,
      payload,
      mutationConfig(),
    );
    return response.data;
  },

  removeTenantPageOverride: async (
    tenantId: string,
    pageKey: string,
    reason: string,
  ) => {
    const response = await apiClient.delete(
      `/modules/tenants/${tenantId}/page-overrides/${encodeURIComponent(pageKey)}`,
      { data: { reason }, ...mutationConfig() },
    );
    return response.data;
  },

  /**
   * Check if tenant has access to a specific module
   */
  checkModuleAccess: async (
    tenantId: string,
    moduleCode: string,
  ): Promise<ModuleAccessResult> => {
    const response = await apiClient.get<ModuleAccessResult>(
      `/modules/tenants/${tenantId}/check/${moduleCode}`,
    );
    return response.data;
  },

  /**
   * Enable a module for a tenant (add override)
   */
  enableModuleForTenant: async (
    tenantId: string,
    payload: EnableModulePayload,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
    }>(`/modules/tenants/${tenantId}/enable`, payload, mutationConfig());
    return response.data;
  },

  /**
   * Disable a module for a tenant (add override)
   */
  disableModuleForTenant: async (
    tenantId: string,
    payload: DisableModulePayload,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
    }>(`/modules/tenants/${tenantId}/disable`, payload, mutationConfig());
    return response.data;
  },

  bulkAssignModules: async (payload: BulkAssignModulesPayload) => {
    const response = await apiClient.post<{
      total: number;
      successful: number;
      failed: number;
      results: Array<{
        tenantId: string;
        moduleCode: string;
        success: boolean;
        error?: string;
      }>;
    }>(`/modules/bulk-assign`, payload, mutationConfig());
    return response.data;
  },

  /**
   * Remove module override for a tenant (revert to plan defaults)
   */
  removeModuleOverride: async (
    tenantId: string,
    moduleCode: string,
    reason: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete<{
      success: boolean;
      message: string;
    }>(`/modules/tenants/${tenantId}/override/${moduleCode}`, {
      data: { reason },
      ...mutationConfig(),
    });
    return response.data;
  },

  /**
   * Update modules for a plan
   */
  updatePlanModules: async (
    planId: string,
    moduleCodes: string[],
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/modules/plans/${planId}`,
      { moduleCodes },
      mutationConfig(),
    );
    return response.data;
  },

  /**
   * Get modules assigned to a plan
   */
  getPlanModules: async (planId: string): Promise<SystemModule[]> => {
    const response = await apiClient.get<SystemModule[]>(
      `/modules/plans/${planId}`,
    );
    return response.data;
  },

  /**
   * @deprecated Page routes are code-owned. Kept temporarily for legacy
   * external callers of SystemModule.routes metadata.
   */
  updateModuleRoutes: async (
    moduleCode: string,
    routes: string[],
  ): Promise<{ success: boolean; updatedRoutes: string[] }> => {
    const response = await apiClient.patch<{
      success: boolean;
      updatedRoutes: string[];
    }>(`/modules/${moduleCode}/routes`, { routes });
    return response.data;
  },

  /**
   * @deprecated Page routes are code-owned. The Admin coverage view is
   * read-only and no longer calls this operation.
   */
  bulkUpdateModuleRoutes: async (
    updates: Array<{ moduleCode: string; routes: string[] }>,
  ): Promise<{ success: boolean; updated: number }> => {
    const response = await apiClient.patch<{
      success: boolean;
      updated: number;
    }>(`/modules/bulk-routes`, { updates });
    return response.data;
  },

  /**
   * @deprecated Customer navigation is code-owned. Kept temporarily for legacy
   * external callers of SystemModule.sidebarConfig metadata.
   */
  updateModuleSidebarConfig: async (
    moduleCode: string,
    sidebarConfig: {
      title: string;
      items: Array<{
        id: string;
        label: string;
        path: string;
        icon?: string;
        children?: Array<{
          id: string;
          label: string;
          path: string;
          icon?: string;
        }>;
      }>;
    },
  ): Promise<{ success: boolean }> => {
    const response = await apiClient.patch<{ success: boolean }>(
      `/modules/${moduleCode}/sidebar-config`,
      { sidebarConfig },
    );
    return response.data;
  },

  /**
   * Update a system module (general update)
   */
  updateModule: async (
    moduleCode: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      sortOrder?: number;
      isActive?: boolean;
      routes?: string[];
      sidebarConfig?: {
        title: string;
        items: Array<{
          id: string;
          label: string;
          path: string;
          icon?: string;
          children?: Array<{
            id: string;
            label: string;
            path: string;
            icon?: string;
          }>;
        }>;
      };
    },
  ): Promise<SystemModule> => {
    const response = await apiClient.patch<SystemModule>(
      `/modules/${moduleCode}`,
      data,
    );
    return response.data;
  },
};
